import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/ApiProvider';
import type {
  BulkAssignmentInput,
  CatalogVersionCreateInput,
  CodeNameInput,
  CoefficientResolutionParams,
  CoefficientValueInput,
  CoefficientVersionCreateInput,
  ComponentTypeCreateInput,
  CostResolutionConfirmInput,
  CostResolutionInput,
  DuplicationInput,
  GeoCreateInput,
  GeoEntityKind,
  GeoListParams,
  GeoUpdateInput,
  ImportInput,
  ReferentialLifecycleAction,
  UnitCostCreateInput,
  UnitCostListParams,
  UnitCostUpdateInput,
} from '../../lib/api';

/**
 * Query keys for the LOT 5 referentials. Everything lives under `['referentiels']`
 * so referential mutations can invalidate in one shot — the server recomputes,
 * the web only re-reads.
 */
export const refKeys = {
  all: ['referentiels'] as const,
  geo: (kind: GeoEntityKind, params: GeoListParams) =>
    ['referentiels', 'geo', kind, params] as const,
  technicalCategories: ['referentiels', 'technical-categories'] as const,
  componentTypes: ['referentiels', 'component-types'] as const,
  measurementUnits: ['referentiels', 'measurement-units'] as const,
  tariffGroups: ['referentiels', 'tariff-groups'] as const,
  catalogs: ['referentiels', 'cost-catalogs'] as const,
  catalogVersions: (catalogId: string) => ['referentiels', 'catalog-versions', catalogId] as const,
  catalogVersion: (id: string) => ['referentiels', 'catalog-version', id] as const,
  unitCosts: (versionId: string, params: UnitCostListParams) =>
    ['referentiels', 'unit-costs', versionId, params] as const,
  coefficientTypes: ['referentiels', 'coefficient-types'] as const,
  coefficientSets: ['referentiels', 'coefficient-sets'] as const,
  coefficientVersions: (setId: string) => ['referentiels', 'coefficient-versions', setId] as const,
  coefficientVersion: (id: string) => ['referentiels', 'coefficient-version', id] as const,
};

/** Shared onSuccess: any referential mutation invalidates the whole referential cache. */
function useInvalidateReferentiels() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: refKeys.all });
}

// --- Géographie ------------------------------------------------------------------

export function useGeoEntities(kind: GeoEntityKind, params: GeoListParams, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.geo(kind, params),
    queryFn: () => api.listGeoEntities(kind, params),
    enabled,
  });
}

export function useCreateGeoEntity(kind: GeoEntityKind) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: GeoCreateInput) => api.createGeoEntity(kind, input),
    onSuccess: invalidate,
  });
}

export function useUpdateGeoEntity(kind: GeoEntityKind) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: GeoUpdateInput }) =>
      api.updateGeoEntity(kind, id, input),
    onSuccess: invalidate,
  });
}

export function useSetGeoEntityActive(kind: GeoEntityKind) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.setGeoEntityActive(kind, id, active),
    onSuccess: invalidate,
  });
}

// --- Types & unités ----------------------------------------------------------------

export function useTechnicalCategories() {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.technicalCategories,
    queryFn: () => api.listTechnicalCategories(),
  });
}

export function useCreateTechnicalCategory() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: CodeNameInput) => api.createTechnicalCategory(input),
    onSuccess: invalidate,
  });
}

export function useComponentTypes(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.componentTypes,
    queryFn: () => api.listComponentTypes(),
    enabled,
  });
}

export function useCreateComponentType() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: ComponentTypeCreateInput) => api.createComponentType(input),
    onSuccess: invalidate,
  });
}

export function useAddComponentTypeUnit() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: ({
      componentTypeId,
      measurementUnitId,
    }: {
      componentTypeId: string;
      measurementUnitId: string;
    }) => api.addComponentTypeUnit(componentTypeId, measurementUnitId),
    onSuccess: invalidate,
  });
}

export function useRemoveComponentTypeUnit() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: ({ componentTypeId, unitId }: { componentTypeId: string; unitId: string }) =>
      api.removeComponentTypeUnit(componentTypeId, unitId),
    onSuccess: invalidate,
  });
}

export function useReferentialMeasurementUnits(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.measurementUnits,
    queryFn: () => api.listReferentialMeasurementUnits(),
    enabled,
  });
}

export function useTariffGroups(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.tariffGroups,
    queryFn: () => api.listTariffGroups(),
    enabled,
  });
}

export function useCreateTariffGroup() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: CodeNameInput) => api.createTariffGroup(input),
    onSuccess: invalidate,
  });
}

export function useAddTariffGroupMembers() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: ({ groupId, componentTypeIds }: { groupId: string; componentTypeIds: string[] }) =>
      api.addTariffGroupMembers(groupId, componentTypeIds),
    onSuccess: invalidate,
  });
}

export function useRemoveTariffGroupMember() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: ({ groupId, componentTypeId }: { groupId: string; componentTypeId: string }) =>
      api.removeTariffGroupMember(groupId, componentTypeId),
    onSuccess: invalidate,
  });
}

// --- Catalogues ----------------------------------------------------------------------

export function useCostCatalogs(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.catalogs,
    queryFn: () => api.listCostCatalogs(),
    enabled,
  });
}

export function useCreateCostCatalog() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: CodeNameInput) => api.createCostCatalog(input),
    onSuccess: invalidate,
  });
}

export function useCatalogVersions(catalogId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.catalogVersions(catalogId),
    queryFn: () => api.listCatalogVersions(catalogId),
    enabled,
  });
}

export function useCreateCatalogVersion(catalogId: string) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: CatalogVersionCreateInput) => api.createCatalogVersion(catalogId, input),
    onSuccess: invalidate,
  });
}

export function useCatalogVersion(id: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.catalogVersion(id),
    queryFn: () => api.getCatalogVersion(id),
    enabled,
  });
}

export function useTransitionCatalogVersion(id: string) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (action: ReferentialLifecycleAction) => api.transitionCatalogVersion(id, action),
    onSuccess: invalidate,
  });
}

export function useUnitCosts(versionId: string, params: UnitCostListParams, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.unitCosts(versionId, params),
    queryFn: () => api.listUnitCosts(versionId, params),
    enabled,
  });
}

export function useCreateUnitCost(versionId: string) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: UnitCostCreateInput) => api.createUnitCost(versionId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateUnitCost() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UnitCostUpdateInput }) =>
      api.updateUnitCost(id, input),
    onSuccess: invalidate,
  });
}

export function useBulkAssignCosts(versionId: string) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: BulkAssignmentInput) => api.bulkAssignCosts(versionId, input),
    onSuccess: invalidate,
  });
}

/** MANDATORY preview step — the apply button stays disabled until this succeeds. */
export function usePreviewDuplication() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: DuplicationInput) => api.previewDuplication(input),
  });
}

export function useApplyDuplication() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: DuplicationInput) => api.applyDuplication(input),
    onSuccess: invalidate,
  });
}

// --- Coefficients ----------------------------------------------------------------------

export function useCoefficientTypes(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.coefficientTypes,
    queryFn: () => api.listCoefficientTypes(),
    enabled,
  });
}

export function useCreateCoefficientType() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: CodeNameInput) => api.createCoefficientType(input),
    onSuccess: invalidate,
  });
}

export function useCoefficientSets(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.coefficientSets,
    queryFn: () => api.listCoefficientSets(),
    enabled,
  });
}

export function useCreateCoefficientSet() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: CodeNameInput) => api.createCoefficientSet(input),
    onSuccess: invalidate,
  });
}

export function useCoefficientVersions(setId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.coefficientVersions(setId),
    queryFn: () => api.listCoefficientVersions(setId),
    enabled,
  });
}

export function useCreateCoefficientVersion(setId: string) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: CoefficientVersionCreateInput) =>
      api.createCoefficientVersion(setId, input),
    onSuccess: invalidate,
  });
}

export function useCoefficientVersion(id: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: refKeys.coefficientVersion(id),
    queryFn: () => api.getCoefficientVersion(id),
    enabled,
  });
}

export function useSetCoefficientValue(versionId: string) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: CoefficientValueInput) => api.setCoefficientValue(versionId, input),
    onSuccess: invalidate,
  });
}

export function useTransitionCoefficientVersion(id: string) {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (action: ReferentialLifecycleAction) =>
      api.transitionCoefficientVersion(id, action),
    onSuccess: invalidate,
  });
}

export function useResolveCoefficients() {
  const api = useApi();
  return useMutation({
    mutationFn: (params: CoefficientResolutionParams) => api.resolveCoefficients(params),
  });
}

// --- Résolution tarifaire (dossier) --------------------------------------------------

export function usePreviewCostResolution(dossierId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (input: CostResolutionInput) => api.previewCostResolution(dossierId, input),
  });
}

export function useConfirmCostResolution(dossierId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (input: CostResolutionConfirmInput) => api.confirmCostResolution(dossierId, input),
  });
}

// --- Import ---------------------------------------------------------------------------

export function usePreviewImport() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: ImportInput) => api.previewImport(input),
  });
}

export function useCommitImport() {
  const api = useApi();
  const invalidate = useInvalidateReferentiels();
  return useMutation({
    mutationFn: (input: ImportInput) => api.commitImport(input),
    onSuccess: invalidate,
  });
}
